// components/Admin/SchemaProvisioningPage.jsx — review and run SharePoint schema provisioning
import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Button, Spinner, Text } from "@fluentui/react-components";
import {
  Database24Regular, DatabaseSearch24Regular, LockClosed24Regular,
  CheckmarkCircle20Regular, Warning20Regular, ErrorCircle20Regular, Info20Regular,
} from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import {
  APP_SCHEMA, SCHEMA_VERSION, provisionSchema, getProvisionToken, loadProvisionResult, isProvisioningOwner, PROVISIONED_EVENT,
} from "../../services/provisioningService";

const STATUS = {
  ok:      { label: "Up to date", color: "#0B6A0B", bg: "#E7F4E4", Icon: CheckmarkCircle20Regular },
  created: { label: "Created",    color: "#005A9E", bg: "#E3F0FF", Icon: Info20Regular },
  updated: { label: "Updated",    color: "#7A3A00", bg: "#FFF1E5", Icon: Info20Regular },
  error:   { label: "Failed",     color: "#A4262C", bg: "#FDE7E9", Icon: ErrorCircle20Regular },
};

const DRY_STATUS = { ...STATUS, created: { ...STATUS.created, label: "Will be created" }, updated: { ...STATUS.updated, label: "Needs changes" } };

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
}

export default function SchemaProvisioningPage() {
  const { instance, accounts } = useMsal();
  const { currentUserEmail, isDemoMode } = useUserAccess();
  const [result, setResult] = useState(loadProvisionResult);
  const [running, setRunning] = useState(null); // "check" | "run"
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");

  if (!isProvisioningOwner(currentUserEmail)) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 14 }}>
        <LockClosed24Regular style={{ fontSize: 48, color: "#D13438" }} />
        <Text size={500} weight="semibold">Access Restricted</Text>
        <Text style={{ color: "#605E5C" }}>SharePoint schema management is limited to the system owner.</Text>
      </div>
    );
  }

  const run = async (dryRun) => {
    setRunning(dryRun ? "check" : "run");
    setError("");
    try {
      const token = await getProvisionToken(instance, accounts, { interactive: true });
      const r = await provisionSchema(token, { dryRun, onProgress: (list) => setProgress(list || "") });
      setResult(r);
      if (!dryRun && r.lists.some((l) => l.status === "created" || l.status === "updated")) {
        window.dispatchEvent(new Event(PROVISIONED_EVENT));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(null);
      setProgress("");
    }
  };

  const meta = result?.dryRun ? DRY_STATUS : STATUS;
  const pendingChanges = result?.lists.reduce((n, l) => n + l.actions.length, 0) || 0;

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <Text style={{ fontSize: 24, fontWeight: 800, color: "#001A3D", display: "block" }}>SharePoint Schema</Text>
          <Text style={{ fontSize: 13, color: "#605E5C" }}>
            Keeps the app's {APP_SCHEMA.length} SharePoint lists in step with the code. Runs automatically when you sign in.
          </Text>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button icon={running === "check" ? <Spinner size="tiny" /> : <DatabaseSearch24Regular />} disabled={!!running || isDemoMode} onClick={() => run(true)}>
            Check only
          </Button>
          <Button appearance="primary" icon={running === "run" ? <Spinner size="tiny" /> : <Database24Regular />} disabled={!!running || isDemoMode} onClick={() => run(false)}>
            Provision now
          </Button>
        </div>
      </div>

      <div style={{ background: "#F0F6FF", border: "1px solid #C7DBFF", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#1B3A6B", lineHeight: 1.7, marginBottom: 16 }}>
        <strong>Additive only.</strong> Missing lists and columns are created, lookup columns are indexed, and JSON columns stuck on single-line text are widened to multi-line.
        Existing columns, data and list settings are never deleted, renamed or retyped. Mismatches are reported below for you to review.
        Requires the <code>Sites.Manage.All</code> delegated permission (admin consent) and Manage Lists rights on the site.
      </div>

      {isDemoMode && (
        <div style={{ background: "#FFF8E1", border: "1px solid #FFE08A", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#6B4E00", marginBottom: 16 }}>
          The app is in demo mode (SharePoint is not reachable), so there is nothing to provision.
        </div>
      )}
      {error && (
        <div style={{ background: "#FDE7E9", border: "1px solid #F1BBBC", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "#A4262C", marginBottom: 16 }}>{error}</div>
      )}
      {running && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#605E5C", marginBottom: 16 }}>
          <Spinner size="tiny" /> {running === "check" ? "Checking" : "Provisioning"} {progress}…
        </div>
      )}

      {result && (
        <Text style={{ fontSize: 12, color: "#8A8886", display: "block", marginBottom: 10 }}>
          {result.dryRun ? `Check run ${fmt(result.ranAt)} — ${pendingChanges} pending change${pendingChanges === 1 ? "" : "s"}` : `Last provisioned ${fmt(result.ranAt)} · schema v${result.version}`}
          {result.version !== SCHEMA_VERSION && " (older schema version — provision again)"}
        </Text>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
        {APP_SCHEMA.map((def) => {
          const entry = result?.lists.find((l) => l.list === def.list);
          const m = entry ? meta[entry.status] : null;
          return (
            <div key={def.list} style={{ background: "#fff", border: "1px solid #EDEBE9", borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <Text style={{ fontWeight: 700, fontSize: 14, color: "#001A3D", display: "block" }}>{def.list}</Text>
                  <Text style={{ fontSize: 12, color: "#8A8886" }}>{def.description} · {def.columns.length} columns · {def.columns.filter((c) => c.indexed).length} indexed</Text>
                </div>
                {m ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: m.bg, color: m.color, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                    <m.Icon style={{ fontSize: 14 }} /> {m.label}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: "#8A8886" }}>Not checked</span>
                )}
              </div>
              {entry?.error && <div style={{ fontSize: 12, color: "#A4262C", marginTop: 10, lineHeight: 1.5 }}>{entry.error}</div>}
              {entry?.actions.length > 0 && (
                <ul style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 12, color: "#323130", lineHeight: 1.7 }}>
                  {entry.actions.map((a) => <li key={a}>{a}</li>)}
                </ul>
              )}
              {entry?.warnings.length > 0 && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                  {entry.warnings.map((w) => (
                    <div key={w} style={{ display: "flex", gap: 6, fontSize: 12, color: "#7A5F00", lineHeight: 1.5 }}>
                      <Warning20Regular style={{ fontSize: 14, flexShrink: 0, marginTop: 2 }} /> {w}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
